"use client"

import React from "react"
import { Boxes, Compass, Database, GitBranch, ListChecks, RefreshCw, Table2 } from "lucide-react"
import { useTranslation, type Translator } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { HubCounts } from "@/hooks/use-hub-counts"

export type HubTabKey = "mappings" | "explorer" | "value-sets" | "target-mapping" | "sources" | "event-sync"

export interface HubTabDefinition {
  key: HubTabKey
  /** String key for the card's label */
  labelKey: TranslationKey
  /** The `?view=` value; null for the home (mappings) */
  view: string | null
  /** Per-card accent, in the Hub's navy-to-slate ramp */
  accent: string
  icon: React.ReactNode
}

export const HUB_TABS: HubTabDefinition[] = [
  { key: "mappings", labelKey: "hub.tabs.mappings", view: null, accent: "#0f214b", icon: <GitBranch className="h-3.5 w-3.5" /> },
  { key: "explorer", labelKey: "hub.tabs.explorer", view: "explorer", accent: "#3f4d6f", icon: <Compass className="h-3.5 w-3.5" /> },
  { key: "value-sets", labelKey: "hub.tabs.valueSets", view: "value-mapping-sets", accent: "#6b768f", icon: <ListChecks className="h-3.5 w-3.5" /> },
  { key: "target-mapping", labelKey: "hub.tabs.targetMapping", view: "target-mapping", accent: "#8a94ab", icon: <Table2 className="h-3.5 w-3.5" /> },
  { key: "sources", labelKey: "hub.tabs.sources", view: "ats-sources", accent: "#4aa06d", icon: <Database className="h-3.5 w-3.5" /> },
  { key: "event-sync", labelKey: "hub.tabs.eventSync", view: "event-sync", accent: "#db9a3c", icon: <RefreshCw className="h-3.5 w-3.5" /> },
]

/** Which tab a URL state belongs to */
export function activeHubTab(view: string | null, mappingName: string | null): HubTabKey {
  if (view === "data-pull") return "explorer"
  const match = HUB_TABS.find((tab) => tab.view !== null && tab.view === view)
  if (match) return match.key
  return mappingName && mappingName !== "selector" ? "mappings" : "mappings"
}

export interface HubTabsProps {
  active: HubTabKey
  counts: HubCounts
  onSelect: (tab: HubTabDefinition) => void
}

function countFor(tab: HubTabKey, counts: HubCounts, { t, n }: Translator): { value: string; sub: string } {
  switch (tab) {
    case "mappings":
      return { value: counts.mappings === null ? "—" : n(counts.mappings), sub: t(("hub.cards.mappings.sub")) }
    case "explorer":
      return { value: counts.entities === null ? "—" : n(counts.entities), sub: t(("hub.cards.explorer.sub")) }
    case "value-sets":
      return { value: counts.valueSets === null ? "—" : n(counts.valueSets), sub: t(("hub.cards.valueSets.sub")) }
    case "target-mapping":
      return { value: t(("hub.cards.targetMapping.value")), sub: t(("hub.cards.targetMapping.sub")) }
    case "sources":
      return {
        value: counts.sources === null ? "—" : n(counts.sources),
        sub: counts.activeSourceCode ? t(("hub.cards.sources.subActive"), { code: counts.activeSourceCode }) : t(("hub.cards.sources.sub")),
      }
    case "event-sync":
      return { value: t(("hub.cards.eventSync.value")), sub: t(("hub.cards.eventSync.sub")) }
  }
}

/**
 * The Timesheet Hub's summary cards as the hub's tool tabs: white cards on the canvas,
 * a per-card accent bar, label, count and a sub-line; the active card is lifted with a
 * navy edge. One row, no wrapping past six.
 */
export function HubTabs({ active, counts, onSelect }: HubTabsProps) {
  const translator = useTranslation()
  const { t } = translator
  return (
    <div className="grid grid-cols-3 gap-3 px-4 pt-3 md:grid-cols-6" role="tablist" aria-label={t("hub.tabs.ariaLabel")}>
      {HUB_TABS.map((tab) => {
        const isActive = tab.key === active
        const count = countFor(tab.key, counts, translator)
        return (
          <button
            key={tab.key}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onSelect(tab)}
            className={`relative flex flex-col items-start gap-0.5 overflow-hidden rounded-lg border bg-card px-3 py-2.5 text-left shadow-card transition-all hover:border-[var(--th-card-border-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              isActive ? "border-primary ring-1 ring-primary" : "border-card-border"
            }`}
          >
            <span className="absolute inset-y-0 left-0 w-1" style={{ background: tab.accent }} aria-hidden />
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--th-section-label)]">
              <span style={{ color: tab.accent }}>{tab.icon}</span>
              {t(tab.labelKey)}
            </span>
            <span className="font-display text-lg font-extrabold leading-tight text-foreground">{count.value}</span>
            <span className="truncate text-[11px] text-muted-foreground">{count.sub}</span>
            {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-fbo-accent" aria-hidden />}
          </button>
        )
      })}
    </div>
  )
}

export { Boxes }
